// The Medical History & Questionnaire, as data.
//
// One list of sections, each a list of questions, read by both the view and
// the edit form so the two cannot drift apart. The field names and option
// codes are exactly those the old two-step form (MedicalStep1 / MedicalStep2)
// sent to api/Profile/UpdateQuestionaire, so saving needs no API change.
//
// Types: "yn" (Yes/No: 271400001 / 271400000), "text", "number", "date",
// "select" (with `options`). `follows` names a yes/no question; the field is
// asked, and counted, only when that answer is Yes. `only: "female" | "male"`
// limits a section or question to that patient.

export const YES = 271400000;
export const NO = 271400001;

const yn = (name, label, extra = {}) => ({ name, label, type: "yn", ...extra });
const text = (name, label, extra = {}) => ({ name, label, type: "text", ...extra });
const detail = (name, follows, label = "If yes, please give details") => ({ name, label, type: "text", follows });

export const SECTIONS = [
  {
    id: "A",
    title: "Section A: Your measurements",
    fields: [
      { name: "heightinMetres", label: "What is your height?", type: "number", unit: "m", step: "0.01", min: 0, max: 3 },
      { name: "weightinKilograms", label: "What is your weight?", type: "number", unit: "kg", step: "0.1", min: 0 },
      text("systolic", "Systolic pressure"),
      text("diastolic", "Diastolic pressure"),
      yn("isThePatientDisabled", "Do you have a disability?"),
      detail("isThePatientDisabledifYesPleaseGiveDetail", "isThePatientDisabled"),
    ],
  },
  {
    id: "D",
    title: "Section D: Gynaecology history",
    only: "female",
    fields: [
      { name: "howoldwereyouwhenyoustartedperiodsmenarch", label: "How old were you when you started periods (menarche)?", type: "number" },
      { name: "regularityofcycle", label: "Regularity of cycle", type: "select", options: [["0", "Regular"], ["1", "Irregular"]] },
      text("howLongAreYourCyclesDays", "How long are your cycles (days)?"),
      { name: "whatIsTheNormalLengthofBleedingDays", label: "What is the normal length of bleeding days?", type: "number" },
      { name: "whatDateWasYourLastMenstrualPeriod", label: "What date was your last menstrual period?", type: "date" },
      text("howLongHaveYouBeenTryingtoGetPregnant", "How long have you been trying to get pregnant (if applicable)?"),
      yn("doYouSufferfromPainfulPeriodsDysmenorrhoea", "Do you suffer from painful periods?"),
      yn("doYouSufferfromHeavyPeriodsMenorrhagia", "Do you suffer from heavy periods?"),
      yn("doYouHaveBleedingBetweenPeriods", "Do you have bleeding between periods?"),
      yn("doYouExperienceBleedingAfterIntercourse", "Do you bleed after sex?"),
      yn("isintercoursepainfuldyspareunia", "Is intercourse painful?"),
      detail("dyspareuniaifyespleasegivedetails", "isintercoursepainfuldyspareunia"),
      yn("vaginaldischargepvdischarge", "Do you have vaginal discharge?"),
      detail("pvdischargeifyespleasegivedetails", "vaginaldischargepvdischarge"),
      yn("haveYouEverHadAnyPreviousPelvicInfections", "Have you ever had any previous pelvic infections?"),
      { name: "whatWasYourLastSmearTestResult", label: "What was the smear test result?", type: "select", options: [["271400000", "Normal"], ["271400001", "Abnormal"]] },
      yn("haveYouEverhadanAbnormalTest", "Have you ever had an abnormal blood test?"),
      detail("hadanAbnormalTestifYesPleaseGiveDetails", "haveYouEverhadanAbnormalTest"),
      yn("haveyouhadanytreatmenttoyourcervix", "Have you ever had treatment to your cervix?"),
      detail("cervixTreatmentifYesPleaseGiveDetails", "haveyouhadanytreatmenttoyourcervix"),
      yn("haveyouhadalargeloopexcisionlletz", "Have you had a large loop excision (LLETZ)?"),
      detail("largeloopexcisiongivedetails", "haveyouhadalargeloopexcisionlletz"),
      yn("haveYouEverHadAnyGynaecologicalTreatment", "Have you ever been diagnosed with a gynaecological condition, e.g. fibroids, endometriosis, cysts?"),
      detail("diagnosedwithaGynaecologicalIfYesPleasegive", "haveYouEverHadAnyGynaecologicalTreatment"),
      yn("areyouCurrentlyUsingAnyContraceptives", "Are you currently using any contraceptives?"),
      detail("contraceptivesIfYesPleaseGiveDetails", "areyouCurrentlyUsingAnyContraceptives"),
      yn("haveyoueverbeenpregnant", "Have you ever been pregnant?"),
      detail("everbeenpregnantifyespleasegivedetail", "haveyoueverbeenpregnant"),
      yn("familyhistoryoffgmvulvalcuttingsurgery", "Family history of FGM (vulval cutting/surgery)"),
    ],
  },
  {
    id: "D",
    title: "Section D: Male reproductive history",
    only: "male",
    fields: [
      yn("haveYouEverHadTwistingofaTesticle", "Have you ever had twisting of a testicle?"),
      yn("haveYouhadMumps", "Have you had mumps?"),
      yn("haveyouhadmaldescent", "Have you had maldescent?"),
      yn("haveyouhadtrauma", "Have you had trauma?"),
      yn("haveyouhadvaricocele", "Have you had varicocele?"),
      yn("haveyouhadinfection", "Have you had infection?"),
      yn("haveyouhadsurgery", "Have you had surgery?"),
      yn("haveyouhadchemotherapy", "Have you had chemotherapy?"),
      yn("haveyoufatheredanypregnanciesbefore", "Have you fathered any pregnancies before?"),
      detail("fatheredpregnancyifyesgivedetail", "haveyoufatheredanypregnanciesbefore"),
      text("pregnancieswithpreviouspartners", "Pregnancies with previous partners"),
      { name: "frequencytiming", label: "Frequency / timing", type: "number" },
      yn("heat", "Does your job expose you to heat?"),
      yn("chemicals", "Does your job expose you to chemicals?"),
      yn("pesticides", "Does your job expose you to pesticides?"),
      yn("radiation", "Does your job expose you to radiation?"),
      yn("erection", "Difficulties with erection?"),
      yn("ejaculation", "Difficulties with ejaculation?"),
      yn("penetration", "Difficulties with penetration?"),
      yn("pain", "Pain with intercourse?"),
      yn("dyspareunia", "Dyspareunia?"),
      yn("doTravelAwayFromHomeOften", "Do you travel away from home often?"),
      text("maleComments", "Comments"),
    ],
  },
  {
    id: "E",
    title: "Section E: Medical and surgical history",
    fields: [
      yn("doYouHaveAnyMedicalConditionsEgAsthmae", "Do you have any medical conditions? e.g. asthma, epilepsy, diabetes, anaemia, colitis"),
      detail("medicalconditionsifyespleaseprovidedetail", "doYouHaveAnyMedicalConditionsEgAsthmae"),
      yn("haveYouEverHadAnySexuallyTransmittedDisease", "Have you ever had any sexually transmitted diseases?"),
      detail("sexuallytransmitteddiseaseIfyesgivedetail", "haveYouEverHadAnySexuallyTransmittedDisease"),
      yn("haveYouTravelledtoaZikaorEbolaAffectedArea", "Have you travelled to a Zika or Ebola affected area in the last six months?"),
      detail("zikaIfYesPleaseGiveDetailsofLocationDates", "haveYouTravelledtoaZikaorEbolaAffectedArea", "If yes, please give location and dates"),
      yn("haveYouHadFertilityInvestigationsinthePast", "Have you had fertility investigations in the past?"),
      detail("fertilityInvesifYesPleaseGiveDetailsDates", "haveYouHadFertilityInvestigationsinthePast", "If yes, please give details, dates and results"),
      yn("haveYouEverHadAssistedConceptionTreatment", "Have you ever had assisted conception treatment before? e.g. ovulation induction, IUI, IVF, ICSI"),
      yn("wasTheTreatmentWiththePartnerYouAreSeeking", "Was the treatment with the partner you are seeking treatment with?", { follows: "haveYouEverHadAssistedConceptionTreatment" }),
      {
        name: "doYouHaveAnyFrozenEggsSpermOrEmbryosin",
        label: "Do you have any frozen eggs, sperm or embryos in storage elsewhere?",
        type: "select",
        options: [["271400000", "No"], ["271400001", "Partner eggs"], ["271400002", "Sperm"], ["271400003", "Donor sperm"], ["271400004", "Embryos"], ["271400005", "Own eggs"]],
      },
      detail("eggsspermorembryosstoredifyespleasegive", null),
    ],
  },
  {
    id: "F",
    title: "Section F: Medications",
    fields: [
      yn("areyoutakinganyMedicationsCurrently", "Are you taking any medications currently?"),
      detail("takingAnyMedicationsIfYesPleaseGiveDetail", "areyoutakinganyMedicationsCurrently"),
      yn("areYouAllergicToAnyMedication", "Are you allergic to any medication?"),
    ],
  },
  {
    id: "G",
    title: "Section G: Family history",
    fields: [
      yn("doyouHaveaFamilyHistoryofMedicalConditions", "Do you have a family history of any medical conditions? e.g. cancer, heart disease, genetic disease"),
      detail("familyHistoryMedicalIfYesPleaseGiveDetail", "doyouHaveaFamilyHistoryofMedicalConditions"),
      text("familyhistoryrelevantgeneticdisorders", "Family history (relevant genetic disorders)"),
      text("familyhistoryofdeepveinthrombosis", "Family history of deep vein thrombosis", { only: "female" }),
      yn("arethecouplerelatedconsanguineous", "Are the couple related / consanguineous?", { only: "female" }),
      text("originofparentspaternal", "Origin of parents (father)"),
      text("originofparentsmaternal", "Origin of parents (mother)"),
      text("originofgrandparentsmaternal", "Origin of grandparents (maternal)"),
      text("originofgrandparentspaternal", "Origin of grandparents (paternal)"),
      text("anymentalhealthissues", "Any mental health issues?", { only: "female" }),
      text("anymentalhealthissues_male", "Any mental health issues?", { only: "male" }),
    ],
  },
  {
    id: "H",
    title: "Section H: Social history",
    fields: [
      yn("doYouSmoke", "Do you smoke?"),
      detail("smokeIfYesIndicateHowManyPerDay", "doYouSmoke", "If yes, how many per day?"),
      yn("doYouDrinkAlcohol", "Do you drink alcohol?"),
      detail("alcoholIfYesHowManyUnitsPerWeek", "doYouDrinkAlcohol", "If yes, how many units per week?"),
      yn("doYouUseReCreationalDrugs", "Do you use recreational drugs?"),
      detail("drugPleaseGiveDetailsandFrequencyofUse", "doYouUseReCreationalDrugs", "Please give details and frequency of use"),
    ],
  },
  {
    id: "I",
    title: "Section I: About your enquiry",
    fields: [
      {
        name: "reasonForContacting",
        label: "Reason for contacting",
        type: "select",
        options: [
          ["271400000", "PGS (PGT-A) Pre Implantation Genetic Screening"],
          ["0", "Primary infertility (no pregnancies)"],
          ["1", "Secondary infertility (previous pregnancies in current relationship)"],
          ["2", "Secondary infertility (previous pregnancies in a different relationship)"],
          ["3", "Recurrent Miscarriage"],
          ["4", "Single person"],
          ["5", "Same sex couple"],
          ["6", "Fertility preservation due to medical reasons"],
          ["7", "Fertility preservation for social reasons"],
          ["8", "PGD to reduce risk of passing on genetic disorder"],
          ["9", "To be an egg donor"],
          ["10", "Egg recipient (to receive donated eggs)"],
        ],
      },
      {
        name: "howDidYouHearAboutUs",
        label: "How did you hear about us?",
        type: "select",
        options: [
          ["271400000", "Medical Referral"], ["271400001", "Recommendation"], ["271400002", "Social Media"], ["271400003", "Radio"],
          ["271400004", "GP"], ["271400005", "Newspaper or Magazine"], ["271400006", "Website"], ["271400007", "Other"],
        ],
      },
      yn("doYouHaveAnyFurtherInformationYouWishtoM", "Do you have any further information you wish to make the clinic aware of?"),
      detail("furtherInformationIfYesPleaseProvideDetails", "doYouHaveAnyFurtherInformationYouWishtoM"),
    ],
  },
];

// A stored value is "no answer" when it is missing, blank, a placeholder, or
// the API's "0001-01-01" for an empty date.
export function isAnswered(field, value) {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  if (s === "" || s === "0001-01-01" || /^--/.test(s)) return false;
  if (field.type === "yn") return Number(s) === YES || Number(s) === NO;
  if (field.type === "number" && field.unit && Number(s) === 0) return false;
  return true;
}

export function displayValue(field, value) {
  if (!isAnswered(field, value)) return null;
  if (field.type === "yn") return Number(value) === YES ? "Yes" : "No";
  if (field.type === "select") {
    const hit = field.options.find(([code]) => String(code) === String(value));
    return hit ? hit[1] : String(value);
  }
  if (field.type === "number" && field.unit) return `${value} ${field.unit}`;
  return String(value);
}

// The questions that apply to this record: the right sex, and a follow-up only
// when its lead question is answered Yes.
export function visibleFields(section, record, gender) {
  return section.fields.filter((f) => {
    if (f.only && f.only !== gender) return false;
    if (f.follows && Number(record?.[f.follows]) !== YES) return false;
    return true;
  });
}

export function sectionsFor(gender) {
  return SECTIONS.filter((s) => !s.only || s.only === gender);
}
